import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import * as XLSX from "xlsx";
import { downloadTemplateXlsx } from "modules/importacion/excelPreview";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useTheme, alpha } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { DataGrid, GridActionsCellItem, useGridApiRef } from "@mui/x-data-grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import MDButton from "components/MDButton";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useApi } from "lib/api";
import { useMarcasEstandar, claveMarca } from "lib/marcasEstandar";
import AvisoMarcasEstandar from "lib/AvisoMarcasEstandar";
import { useMaterialUIController } from "context";
import { ocultaCostos } from "lib/seguridad/permisosPorModulo";
import {
  imprimirFacturaCxp,
  imprimirCompraInformal,
} from "modules/cuentasxpagar/impresionfacturacxp";
import { DocumentoField } from "components/financepro";
import {
  EditorCuotas,
  generarCuotas,
  manana,
  problemaCuotas,
} from "modules/cuentasxpagar/planpagoscxp";
import { documentoValido } from "lib/documento";
import AlertaRed from "modules/dataorbit/alertaventa";


function parseNumeroLoose(val) {
  const t = String(val ?? "")
    .trim()
    .replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) && !Number.isNaN(n) ? n : null;
}

/** Monto para celdas; evita ruido de coma flotante. */
function formatMonto(n) {
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n * 100) / 100);
}

function normalizeRow(r, cat) {
  if (!r || typeof r !== "object") {
    return {
      chasis: "",
      marca: "",
      modelo: "",
      color: "",
      maquina: "",
      ano: "",
      cilindraje: "",
      precio: "",
      precioMinimo: "",
      costo: "",
      precioBase: 0,
      costoBase: 0,
      sucursal: "",
    };
  }
  let precioStr =
    r.precio != null ? String(r.precio) : r.price != null ? String(r.price) : "";
  // Catálogo de precios (Configuración → Catálogos): las celdas de precio y
  // mínimo que el documento traiga vacías (o en 0) se rellenan en la grilla
  // con lo del catálogo para esa marca/modelo. Lo que el documento sí trae,
  // manda.
  if (!(parseNumeroLoose(precioStr) > 0) && cat && cat.precio > 0) {
    precioStr = formatMonto(cat.precio);
  }
  const costoStr = r.costo != null ? String(r.costo) : r.cost != null ? String(r.cost) : "";
  let precioMinimoRaw =
    r.mprecio != null
      ? String(r.mprecio)
      : r.precioMinimo != null
        ? String(r.precioMinimo)
        : "";
  if (!(parseNumeroLoose(precioMinimoRaw) > 0) && cat && cat.mprecio > 0) {
    precioMinimoRaw = formatMonto(cat.mprecio);
  }
  const precioMinimoStr = precioMinimoRaw.trim() !== "" ? precioMinimoRaw : precioStr;
  const precioBase = parseNumeroLoose(precioStr) ?? 0;
  const costoBase = parseNumeroLoose(costoStr) ?? 0;
  return {
    chasis: String(r.chasis ?? r.chassis ?? ""),
    marca: String(r.marca ?? r.brand ?? ""),
    modelo: String(r.modelo ?? r.model ?? ""),
    color: String(r.color ?? ""),
    maquina: r.maquina != null ? String(r.maquina) : "",
    ano: r.ano != null ? String(r.ano) : "",
    cilindraje: r.cilindraje != null ? String(r.cilindraje) : "",
    precio: precioStr,
    precioMinimo: precioMinimoStr,
    costo: costoStr,
    precioBase,
    costoBase,
    sucursal: String(r.sucursal ?? ""),
  };
}

/** Entrada del catálogo de precios para la marca/modelo, o null. Exacta
 *  primero y, si no, sin importar espacios/guiones ("PLATINA-125" encuentra
 *  "PLATINA 125"), igual que el backend al guardar. */
function precioCatalogoDe(lista, marca, modelo) {
  const m = String(marca ?? "").trim().toUpperCase();
  const d = String(modelo ?? "").trim().toUpperCase();
  if (!m || !d || !Array.isArray(lista)) return null;
  const exacta = lista.find((c) => c.marca === m && c.modelo === d);
  if (exacta) return exacta;
  const km = claveMarca(m);
  const kd = claveMarca(d);
  return lista.find((c) => claveMarca(c.marca) === km && claveMarca(c.modelo) === kd) || null;
}

function esNumeroValido(val) {
  const t = String(val ?? "")
    .trim()
    .replace(",", ".");
  if (t === "") return false;
  const n = Number(t);
  return Number.isFinite(n) && !Number.isNaN(n);
}

function parseNumero(val) {
  const t = String(val ?? "")
    .trim()
    .replace(",", ".");
  return Number(t);
}

/** Problemas de una fila para guardar: precio, precio mín., costo, marca y modelo
 *  obligatorios; también chasis, color y sucursal. Año y cilindraje solo cuando
 *  están habilitados en varinicial. Devuelve la lista de faltantes (vacía si ok). */
function problemasFila(row, cfg) {
  const t = (v) => String(v ?? "").trim();
  const faltas = [];
  if (t(row.chasis) === "") faltas.push("chasis");
  if (t(row.marca) === "") faltas.push("marca");
  if (t(row.modelo) === "") faltas.push("modelo");
  if (t(row.color) === "") faltas.push("color");
  if (t(row.precio) === "") faltas.push("precio");
  if (!esNumeroValido(row.precioMinimo)) faltas.push("precio mínimo");
  if (t(row.costo) === "") faltas.push("costo");
  if (t(row.sucursal) === "") faltas.push("sucursal");
  return faltas;
}

/** Plantilla de productos de la pantalla de importación de datos
 *  (importacionDatos.js). Si el Excel trae estos encabezados, las filas se
 *  ingresan todas directo a la grilla, sin pasar por la IA. */
const PLANTILLA_PRODUCTOS_COLUMNS = [
  "Chasis",
  "marca",
  "modelo",
  "color",
  "maquina",
  "costo",
  "precio min",
  "precio",
];

/** Chasis normalizado para comparar contra los que rechaza el backend. */
const normalizarChasis = (v) => String(v ?? "").trim().toUpperCase();

const normalizarEncabezado = (v) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/** Encabezado normalizado -> campo del item. Año, cilindraje, sucursal y
 *  máquina son opcionales: se mapean si vienen, pero no son requeridos (los
 *  Excel de plantillas anteriores siguen siendo válidos). */
const PLANTILLA_ALIAS = {
  chasis: "chasis",
  marca: "marca",
  modelo: "modelo",
  color: "color",
  costo: "costo",
  "precio min": "mprecio",
  "precio minimo": "mprecio",
  mprecio: "mprecio",
  precio: "precio",
  ano: "ano",
  cilindraje: "cilindraje",
  sucursal: "sucursal",
  maquina: "maquina",
  "no maquina": "maquina",
  "no. maquina": "maquina",
  "numero de maquina": "maquina",
};

const PLANTILLA_REQUERIDOS = ["chasis", "marca", "modelo", "color", "costo", "mprecio", "precio"];

/** Reconocimiento tolerante de la plantilla: ignora mayúsculas, acentos y el
 *  orden, y permite columnas extra desconocidas. Si la matriz (fila 0 =
 *  encabezados) trae las 7 columnas de la plantilla, devuelve TODAS las filas
 *  como items listos para la grilla; si no coincide, devuelve null. */
function itemsDesdePlantilla(matrix) {
  if (!Array.isArray(matrix) || matrix.length === 0) return null;
  const campos = (matrix[0] || []).map(
    (h) => PLANTILLA_ALIAS[normalizarEncabezado(h)] ?? null
  );
  const presentes = new Set(campos.filter(Boolean));
  if (!PLANTILLA_REQUERIDOS.every((c) => presentes.has(c))) return null;
  const items = [];
  for (let i = 1; i < matrix.length; i++) {
    const fila = matrix[i];
    if (!Array.isArray(fila)) continue;
    if (!fila.some((c) => String(c ?? "").trim() !== "")) continue;
    const item = {};
    campos.forEach((campo, j) => {
      if (campo && !(campo in item)) item[campo] = fila[j] ?? "";
    });
    items.push(item);
  }
  return items;
}

/** Empata la sucursal que trae el Excel contra las opciones del selector:
 *  por etiqueta completa ("2-Veron"), por código ("2") o por nombre
 *  ("Veron"), sin distinguir mayúsculas. Si no empata, devuelve el valor
 *  tal cual para que el usuario lo vea y lo corrija en la grilla. */
function empatarSucursal(valor, opciones) {
  const v = String(valor ?? "").trim();
  if (v === "") return "";
  const vLower = v.toLowerCase();
  for (const o of opciones) {
    const label = String(o?.label ?? "");
    const partes = label.split("-");
    if (
      label.toLowerCase() === vLower ||
      partes[0].trim().toLowerCase() === vLower ||
      partes.slice(1).join("-").trim().toLowerCase() === vLower
    ) {
      return label;
    }
  }
  return v;
}

const inventarioBulkColumns = [
  { field: "chasis", headerName: "Chasis", editable: true, flex: 1.2, minWidth: 140 },
  { field: "marca", headerName: "Marca", editable: true, flex: 0.7, minWidth: 90 },
  { field: "modelo", headerName: "Modelo", editable: true, flex: 1, minWidth: 120 },
  { field: "color", headerName: "Color", editable: true, flex: 0.6, minWidth: 80 },
  { field: "maquina", headerName: "Máquina", editable: true, flex: 0.9, minWidth: 110 },
  { field: "precio", headerName: "Precio", editable: true, flex: 0.8, minWidth: 100 },
  {
    field: "precioMinimo",
    headerName: "Precio mín",
    editable: true,
    flex: 0.75,
    minWidth: 100,
  },
  { field: "costo", headerName: "Costo", editable: true, flex: 0.8, minWidth: 100 },
];

function SectionHeader({ icon: Icon, title, subtitle, action }) {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: "divider",
        bgcolor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.primary.main, 0.12)
            : theme.palette.grey[50],
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.primary.main, 0.2)
              : alpha(theme.palette.primary.main, 0.12),
          color: "primary.main",
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box>
        <Typography variant="subtitle2" fontWeight={600} color="text.primary">
          {title}
        </Typography>
        {subtitle != null && subtitle !== "" && (
          <Typography variant="caption" color="text.secondary" component="div">
            {subtitle}
          </Typography>
        )}
      </Box>
      {action ? <Box sx={{ ml: "auto", flexShrink: 0 }}>{action}</Box> : null}
    </Stack>
  );
}

// El tema global deja los select sin relleno vertical (padding 0 !important)
// y en tamaño small quedan más bajos que los demás campos: se les devuelve el
// mismo relleno que los inputs small del tema (10px).
// El override del tema va sobre `.css-x.MuiSelect-select` (dos clases) y se
// inserta DESPUÉS del sx del TextField, así que con la misma especificidad
// ganaba el del tema: el selector lleva una clase más. El alto del contenido
// es el del input (1.4375em) para que mida lo mismo que los demás campos.
const selectSmallSx = {
  "& .MuiOutlinedInput-root .MuiSelect-select": {
    padding: "10px 12px !important",
    minHeight: "unset",
    height: "1.4375em",
    lineHeight: "1.4375em",
  },
};

function InventariobulkModal({ open, onClose }) {
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  // Rol con "ocultarcostos": sin columna Costo ni valor total del recibo, y
  // sin poder guardar el alta (véase handleGuardar).
  const sinCostos = ocultaCostos(controller.roleDetalles);
  const theme = useTheme();
  /** @type {[{ id: string, file: File, previewUrl: string }]} */
  const [imagenes, setImagenes] = useState([]);
  /** Excel cargados: csv es lo que se manda a la IA; preview, la matriz para
   *  la vista previa. No se mezclan con imágenes: al elegir un tipo se
   *  descarta el otro. */
  const [excels, setExcels] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  // Switch "Estandarizar marcas y modelos": aviso de las filas que se
  // guardarán con la escritura estándar.
  const estandar = useMarcasEstandar();
  // "Usar" en una sugerencia: corrige todas las filas escritas igual.
  const usarSugerenciaMarca = (par, sug) =>
    setRows((prev) =>
      prev.map((r) =>
        String(r.marca ?? "").trim() === par.marca && String(r.modelo ?? "").trim() === par.modelo
          ? { ...r, marca: sug.marca, modelo: sug.modelo }
          : r
      )
    );
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [step, setStep] = useState(1);
  // Plan de pagos al suplidor (solo compra a crédito, no consignada): cuotas
  // que suman el valor total; se graban junto con la factura.
  const [planActivo, setPlanActivo] = useState(false);
  const [planCuotas, setPlanCuotas] = useState([]);
  const [formFactura, setFormFactura] = useState({
    suplidor: "",
    suplidorId: "",
    numFactura: "",
    fecha: "",
    tasa: "",
    concepto: "",
    consignado: false,
    usados: false,
    desarmados: false,
    // Condición de la compra: CREDITO queda por pagar; CONTADO se paga
    // completa al guardar (pago a suplidor por el total, con su forma de pago).
    tipodefactura: "CREDITO",
    tipodepago: "Efectivo",
    banco: "",
    referencia: "",
    // Multi-empresa: toda la factura entra a una sola empresa (default 1).
    empresa: "1",
  });
  const [sucursalOptions, setSucursalOptions] = useState([]);
  /** Sucursal global del lote: al elegirla se aplica a TODAS las filas (pisa
   *  la del Excel) y a las que se extraigan después; vacía = cada fila con la
   *  suya (Excel o la del usuario). Las filas siguen editables una a una. */
  const [sucursalGlobal, setSucursalGlobal] = useState("");
  const [multiempresaBulk, setMultiempresaBulk] = useState({ activo: false, empresas: [] });
  const [configBulk, setConfigBulk] = useState({ pideAno: false, pideCilindraje: false });
  // Formas de pago de la compra de contado (las de Pagos a Suplidores).
  const [tiposPago, setTiposPago] = useState(["Efectivo", "Tarjeta de Credito", "Transferencia"]);
  // Catálogo de precios por marca/modelo (rellena celdas vacías de la grilla).
  const [preciosCatalogo, setPreciosCatalogo] = useState([]);
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
  const [savePago, setSavePago] = useState(null);
  const [saveInformal, setSaveInformal] = useState("");
  /** Chasis rechazados al guardar (ya existen en inventario o vienen
   *  repetidos en el payload). Pintan su fila en rojo; como el match es por
   *  valor, corregir el chasis en la grilla apaga el resaltado solo. */
  const [chasisConflicto, setChasisConflicto] = useState([]);
  /** Compra informal (condición INFORMAL): vendedor sin factura, buscado por
   *  documento; el número CI-000001 lo asigna el backend al guardar. */
  const VENDEDOR_VACIO = { documento: "", tipo: "cedula", nombre: "", telefono: "", direccion: "" };
  const [vendedor, setVendedor] = useState(VENDEDOR_VACIO);
  const [vendedorOrigen, setVendedorOrigen] = useState("");
  /** Situación de cada chasis en la compra informal: nuevo, recompra
   *  (vendido antes, vuelve a entrar) o bloqueado (con el motivo). */
  const [situaciones, setSituaciones] = useState([]);
  const inputRef = useRef(null);
  const excelInputRef = useRef(null);
  // Una grilla por paso; se usan para abrir la fila agregada a mano en edición.
  const gridPaso1Ref = useGridApiRef();
  const gridPaso2Ref = useGridApiRef();

  /** Las filas digitadas a mano (manual: true) sobreviven a cargar, quitar o
   *  volver a extraer archivos; las extraídas se reemplazan como siempre. */
  const quitarExtraidas = () => setRows((prev) => prev.filter((r) => r.manual));

  const resetState = () => {
    setImagenes((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      return [];
    });
    setExcels([]);
    setSelectedId(null);
    setError(null);
    setResult(null);
    setRows([]);
    setChasisConflicto([]);
    setVendedor(VENDEDOR_VACIO);
    setVendedorOrigen("");
    setSituaciones([]);
    setStep(1);
    setPlanActivo(false);
    setPlanCuotas([]);
    setFormFactura({
      suplidor: "",
      suplidorId: "",
      numFactura: "",
      fecha: "",
      tasa: "",
      concepto: "",
      consignado: false,
      tipodefactura: "CREDITO",
      tipodepago: "Efectivo",
      banco: "",
      referencia: "",
      empresa: "1",
    });
    setSucursalOptions([]);
    setLoading(false);
    if (inputRef.current) inputRef.current.value = "";
    if (excelInputRef.current) excelInputRef.current.value = "";
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const onPickFile = (e) => {
    const list = e.target.files ? Array.from(e.target.files) : [];
    setError(null);
    setResult(null);
    quitarExtraidas();
    setStep(1);
    if (list.length === 0) return;
    const valid = list.filter((f) => f.type.startsWith("image/"));
    if (valid.length === 0) {
      setError("Seleccione archivos de imagen.");
      e.target.value = "";
      return;
    }
    if (valid.length < list.length) {
      setError("Algunos archivos no son imagen; se omitieron.");
    }
    const nuevas = valid.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setExcels([]);
    setImagenes((prev) => [...prev, ...nuevas]);
    setSelectedId(nuevas[nuevas.length - 1].id);
    e.target.value = "";
  };

  const esArchivoExcel = (f) =>
    /\.(xlsx|xls|csv)$/i.test(f.name) ||
    [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
    ].includes(f.type);

  const onPickExcel = async (e) => {
    const list = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = "";
    setError(null);
    setResult(null);
    quitarExtraidas();
    setStep(1);
    if (list.length === 0) return;
    const valid = list.filter(esArchivoExcel);
    if (valid.length === 0) {
      setError("Seleccione archivos Excel (.xlsx, .xls o .csv).");
      return;
    }
    if (valid.length < list.length) {
      setError("Algunos archivos no son Excel; se omitieron.");
    }
    try {
      const nuevos = [];
      for (const file of valid) {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const partes = [];
        let preview = [];
        let matrizPrimera = null;
        workbook.SheetNames.forEach((name) => {
          const ws = workbook.Sheets[name];
          if (!ws) return;
          const csv = XLSX.utils.sheet_to_csv(ws, { blankrows: false });
          if (csv.trim() === "") return;
          partes.push(
            workbook.SheetNames.length > 1 ? `--- Hoja: ${name} ---\n${csv}` : csv
          );
          if (matrizPrimera == null) {
            matrizPrimera = XLSX.utils.sheet_to_json(ws, {
              header: 1,
              blankrows: false,
              defval: "",
            });
            preview = matrizPrimera.slice(0, 100);
          }
        });
        if (partes.length === 0) {
          throw new Error(`El archivo ${file.name} no tiene datos.`);
        }
        // Si la primera hoja usa la plantilla de productos, las filas quedan
        // listas aquí y «Extraer» las ingresa todas sin llamar a la IA.
        const items = itemsDesdePlantilla(matrizPrimera);
        nuevos.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          file,
          csv: partes.join("\n\n"),
          preview,
          items,
        });
      }
      setImagenes((prev) => {
        prev.forEach((img) => URL.revokeObjectURL(img.previewUrl));
        return [];
      });
      setExcels((prev) => [...prev, ...nuevos]);
      setSelectedId(nuevos[nuevos.length - 1].id);
    } catch (err) {
      setError(err.message || "No se pudo leer el archivo Excel.");
    }
  };

  const removeImagen = (id) => {
    setImagenes((prev) => {
      const hit = prev.find((x) => x.id === id);
      if (!hit) return prev;
      URL.revokeObjectURL(hit.previewUrl);
      return prev.filter((x) => x.id !== id);
    });
    setResult(null);
    quitarExtraidas();
    setStep(1);
  };

  const removeExcel = (id) => {
    setExcels((prev) => prev.filter((x) => x.id !== id));
    setResult(null);
    quitarExtraidas();
    setStep(1);
  };

  const limpiarArchivos = () => {
    setImagenes((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      return [];
    });
    setExcels([]);
    setSelectedId(null);
    setResult(null);
    quitarExtraidas();
    setStep(1);
  };

  useEffect(() => {
    const lista = imagenes.length > 0 ? imagenes : excels;
    if (lista.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((sid) =>
      sid != null && lista.some((x) => x.id === sid) ? sid : lista[0].id
    );
  }, [imagenes, excels]);

  useEffect(() => {
    if (!open) return;
    post("/api/inventariobulk/suppliers")
      .then(({ ok, data }) => {
        const options = ok && Array.isArray(data?.suppliers)
          ? data.suppliers
              .filter((s) => s && s.label)
              .map((s) => ({ id: String(s.id ?? ""), label: String(s.label) }))
          : [];
        setSupplierOptions(options);
        setMultiempresaBulk({
          activo: !!(ok && data?.multiempresa),
          empresas: ok && Array.isArray(data?.empresas) ? data.empresas : [],
        });
      })
      .catch(() => {
        setSupplierOptions([]);
        setMultiempresaBulk({ activo: false, empresas: [] });
      });
  }, [open, post]);

  useEffect(() => {
    if (!open) return;
    post("/api/inventariobulk/config")
      .then(({ ok, data }) => {
        const cfg = ok && data?.config ? data.config : {};
        setConfigBulk({
          pideAno: !!cfg.pideAno,
          pideCilindraje: !!cfg.pideCilindraje,
        });
        setPreciosCatalogo(ok && Array.isArray(data?.precioscatalogo) ? data.precioscatalogo : []);
        if (ok && Array.isArray(data?.tiposPago) && data.tiposPago.length) setTiposPago(data.tiposPago);
      })
      .catch(() => {
        setConfigBulk({ pideAno: false, pideCilindraje: false });
        setPreciosCatalogo([]);
      });
  }, [open, post]);

  useEffect(() => {
    if (!open) return;
    post("/api/recuperasucursales")
      .then(({ ok, data }) => {
        const list = ok && Array.isArray(data?.sucursales) ? data.sucursales : [];
        setSucursalOptions(list);
      })
      .catch(() => {
        setSucursalOptions([]);
      });
  }, [open, post]);

  const defaultSucursalLabel = useMemo(() => {
    const raw = String(localStorage.getItem("sucursal") || "").trim();
    if (!raw) return "";
    const exact = sucursalOptions.find((o) => String(o?.label ?? "") === raw);
    if (exact) return String(exact.label);
    const prefix = raw.split("-")[0].trim();
    if (prefix) {
      const byPrefix = sucursalOptions.find(
        (o) => String(o?.label ?? "").split("-")[0].trim() === prefix
      );
      if (byPrefix) return String(byPrefix.label);
    }
    return raw;
  }, [sucursalOptions]);

  useEffect(() => {
    if (!defaultSucursalLabel) return;
    setRows((prev) => {
      if (prev.length === 0) return prev;
      let changed = false;
      const next = prev.map((r) => {
        if (!String(r.sucursal ?? "").trim()) {
          changed = true;
          return { ...r, sucursal: defaultSucursalLabel };
        }
        return r;
      });
      return changed ? next : prev;
    });
  }, [defaultSucursalLabel]);

  const handleSubmit = async () => {
    if (imagenes.length === 0 && excels.length === 0) {
      setError("Seleccione al menos una imagen o un archivo Excel.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    quitarExtraidas();
    setStep(1);
    const combined = [];
    try {
      for (let i = 0; i < excels.length; i++) {
        // Archivos con la plantilla de productos: filas ya parseadas, sin IA.
        if (Array.isArray(excels[i].items)) {
          combined.push(...excels[i].items);
          continue;
        }
        const { ok, data } = await post("/api/extract-motorcycles-excel", {
          texto: excels[i].csv,
        });
        if (!ok) {
          throw new Error(data?.Error || `Error en ${excels[i].file.name}`);
        }
        const items = Array.isArray(data.items) ? data.items : [];
        combined.push(...items);
      }
      for (let i = 0; i < imagenes.length; i++) {
        const fd = new FormData();
        fd.append("image", imagenes[i].file);
        const res = await fetch(
          process.env.REACT_APP_SERVIDOR + "/api/extract-motorcycles",
          { method: "POST", body: fd }
        );
        if (!res.ok) {
          const t = await res.text();
          throw new Error(t || res.statusText || `Error imagen ${i + 1}`);
        }
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];
        combined.push(...items);
      }
      setResult({ items: combined });
      const extraidas = combined.map((r, i) => {
          const nr = normalizeRow(
            r,
            precioCatalogoDe(preciosCatalogo, r?.marca ?? r?.brand, r?.modelo ?? r?.model)
          );
          return {
            id: i,
            ...nr,
            // La sucursal global del lote manda sobre todo; sin ella, la del
            // Excel (plantilla); sin ninguna, la del usuario.
            sucursal:
              sucursalGlobal ||
              empatarSucursal(nr.sucursal, sucursalOptions) ||
              defaultSucursalLabel,
          };
        });
      setRows((prev) => [...prev.filter((r) => r.manual), ...extraidas]);
    } catch (err) {
      setError(err.message || "Error al procesar los archivos.");
    } finally {
      setLoading(false);
    }
  };

  /** Fila vacía al inicio de la grilla, abierta en el chasis para digitar
   *  (compras sin factura ni listado que extraer). */
  const agregarChasisManual = (apiRef) => {
    const id = `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setRows((prev) => [
      {
        id,
        manual: true,
        ...normalizeRow(null),
        sucursal: sucursalGlobal || defaultSucursalLabel,
      },
      ...prev,
    ]);
    setTimeout(() => {
      try {
        apiRef.current?.scrollToIndexes({ rowIndex: 0 });
        apiRef.current?.startCellEditMode({ id, field: "chasis" });
      } catch (e) {
        // La grilla aún no montó la fila: queda para doble clic.
      }
    }, 50);
  };

  const processRowUpdate = useCallback(
    (newRow, oldRow) => {
      const tasaVal = esNumeroValido(formFactura.tasa) ? parseNumero(formFactura.tasa) : null;
      let precioBase = newRow.precioBase ?? oldRow?.precioBase ?? 0;
      let costoBase = newRow.costoBase ?? oldRow?.costoBase ?? 0;

      if (newRow.precio !== oldRow?.precio) {
        const p = parseNumeroLoose(newRow.precio);
        const pv = p ?? 0;
        precioBase = pv;
      }
      if (newRow.costo !== oldRow?.costo) {
        const c = parseNumeroLoose(newRow.costo);
        const cv = c ?? 0;
        costoBase = tasaVal != null && tasaVal !== 0 ? cv / tasaVal : cv;
      }

      const out = {
        ...newRow,
        chasis: String(newRow.chasis ?? "").trim().toUpperCase(),
        precioBase,
        costoBase,
      };
      // Precio y mínimo del catálogo al completar marca/modelo, si la fila
      // aún no los trae (filas digitadas a mano o extraídas sin precio).
      if (newRow.marca !== oldRow?.marca || newRow.modelo !== oldRow?.modelo) {
        const cat = precioCatalogoDe(preciosCatalogo, newRow.marca, newRow.modelo);
        if (cat && !(parseNumeroLoose(newRow.precio) > 0) && cat.precio > 0) {
          precioBase = cat.precio;
          out.precioBase = precioBase;
        }
        if (cat && !(parseNumeroLoose(newRow.precioMinimo) > 0) && cat.mprecio > 0) {
          out.precioMinimo = formatMonto(cat.mprecio);
        }
      }
      if (tasaVal != null && tasaVal !== 0) {
        out.precio = formatMonto(precioBase);
        out.costo = formatMonto(costoBase * tasaVal);
      } else {
        out.precio = formatMonto(precioBase);
        out.costo = formatMonto(costoBase);
      }
      // Una celda vacía sigue vacía (no "0"), para que la validación de la
      // fila la siga pidiendo; pasa sobre todo en las filas digitadas a mano.
      const vacia = (v) => String(v ?? "").trim() === "";
      if (vacia(newRow.precio) && !(precioBase > 0)) out.precio = "";
      if (vacia(newRow.costo) && !(costoBase > 0)) out.costo = "";
      setRows((prev) => prev.map((row) => (row.id === newRow.id ? out : row)));
      return out;
    },
    [formFactura.tasa, preciosCatalogo]
  );

  const inventarioBulkGridColumns = useMemo(
    () => [
      ...inventarioBulkColumns.slice(0, 5),
      ...(configBulk.pideAno
        ? [{ field: "ano", headerName: "Año", editable: true, flex: 0.5, minWidth: 70 }]
        : []),
      ...(configBulk.pideCilindraje
        ? [
            {
              field: "cilindraje",
              headerName: "Cilindraje",
              editable: true,
              flex: 0.6,
              minWidth: 90,
            },
          ]
        : []),
      ...inventarioBulkColumns.slice(5).filter((c) => !(sinCostos && c.field === "costo")),
      {
        field: "sucursal",
        headerName: "Sucursal",
        type: "singleSelect",
        editable: true,
        valueOptions: sucursalOptions.map((o) => ({
          value: String(o.label),
          label: String(o.label),
        })),
        flex: 1.15,
        minWidth: 200,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 52,
        align: "right",
        headerAlign: "right",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        getActions: (params) => [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteOutlineIcon fontSize="small" />}
            label="Eliminar fila"
            onClick={() => {
              setRows((prev) => prev.filter((row) => row.id !== params.id));
            }}
          />,
        ],
      },
    ],
    [sucursalOptions, configBulk, sinCostos]
  );

  /** Suma de la columna Costo de todas las filas (valor total del recibo). */
  const valorTotalSumaCostos = useMemo(() => {
    let s = 0;
    for (const row of rows) {
      const c = parseNumeroLoose(row.costo);
      if (c != null && Number.isFinite(c)) s += c;
    }
    return s;
  }, [rows]);

  // QA: avisar el número de factura duplicado al digitarlo, no al guardar.
  // El número es único en toda la base, sin importar el suplidor.
  const [facturaDuplicada, setFacturaDuplicada] = useState(false);
  const [facturaDuplicadaDe, setFacturaDuplicadaDe] = useState("");
  const verificarFacturaDuplicada = async () => {
    const nfactura = formFactura.numFactura.trim();
    if (!nfactura) {
      setFacturaDuplicada(false);
      setFacturaDuplicadaDe("");
      return;
    }
    const { ok, data } = await post("/api/inventariobulk/checkfactura", {
      numFactura: nfactura,
    });
    setFacturaDuplicada(!!(ok && data.existe));
    setFacturaDuplicadaDe(ok && data.existe ? String(data.suplidor || "") : "");
  };

  const esInformal = formFactura.tipodefactura === "INFORMAL";
  const planAplica =
    formFactura.tipodefactura === "CREDITO" && !formFactura.consignado && !sinCostos;
  const conPlan = planAplica && planActivo;
  const documentoVendedorCompleto = documentoValido(vendedor.documento, vendedor.tipo);

  // Precarga del vendedor por documento: solo rellena lo que esté vacío.
  useEffect(() => {
    if (!esInformal || !documentoVendedorCompleto) {
      setVendedorOrigen("");
      return undefined;
    }
    let vivo = true;
    const t = setTimeout(async () => {
      const { ok, data } = await post("/api/inventariobulk/vendedor", {
        documento: vendedor.documento,
      });
      if (!vivo) return;
      const v = ok ? data?.vendedor : null;
      setVendedorOrigen(v ? v.origen : "");
      if (v) {
        setVendedor((prev) => ({
          ...prev,
          nombre: prev.nombre || v.nombre || "",
          telefono: prev.telefono || v.telefono || "",
          direccion: prev.direccion || v.direccion || "",
        }));
      }
    }, 400);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [esInformal, documentoVendedorCompleto, vendedor.documento, post]);

  // Verifica los chasis de la compra informal (recompras y bloqueados).
  const claveChasis = rows
    .map((r) => normalizarChasis(r.chasis))
    .filter(Boolean)
    .join("|");
  useEffect(() => {
    if (!esInformal || step !== 2 || !claveChasis) {
      setSituaciones([]);
      return undefined;
    }
    let vivo = true;
    const t = setTimeout(async () => {
      const { ok, data } = await post("/api/inventariobulk/verificarchasis", {
        chasis: claveChasis.split("|"),
      });
      if (vivo) setSituaciones(ok && Array.isArray(data?.chasis) ? data.chasis : []);
    }, 400);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [esInformal, step, claveChasis, post]);

  const recompras = situaciones.filter((x) => x.situacion === "recompra");
  const bloqueados = situaciones.filter((x) => x.situacion === "bloqueado");

  /** Lista de errores del paso 2; vacía cuando todo está listo para guardar. */
  const erroresPaso2 = () => {
    const errores = [];
    if (rows.length === 0) {
      errores.push("No hay filas para guardar. Vuelva al paso 1 y extraiga los datos.");
      return errores;
    }
    const f = formFactura;
    if (f.tipodefactura === "INFORMAL") {
      if (!documentoVendedorCompleto) errores.push("Vendedor: el documento está incompleto.");
      if (!vendedor.nombre.trim()) errores.push("Vendedor: falta el nombre.");
      if (!vendedor.telefono.trim()) errores.push("Vendedor: falta el teléfono.");
      bloqueados.forEach((b) => errores.push(`Chasis ${b.chasis}: ${b.detalle}.`));
    } else {
      if (f.suplidor.trim() === "") errores.push("Factura: falta el suplidor.");
      if (f.numFactura.trim() === "") errores.push("Factura: falta el número de factura.");
    }
    if (f.tipodefactura !== "INFORMAL" && facturaDuplicada)
      errores.push(
        `Factura: el número ya está registrado en el sistema${
          facturaDuplicadaDe ? ` (${facturaDuplicadaDe})` : ""
        }.`
      );
    if (f.fecha.trim() === "") errores.push("Factura: falta la fecha.");
    if (!esNumeroValido(f.tasa)) errores.push("Factura: la tasa debe ser un número.");
    if (f.tipodefactura !== "INFORMAL" && f.concepto.trim() === "")
      errores.push("Factura: falta el concepto.");
    if (f.tipodefactura === "CONTADO" || f.tipodefactura === "INFORMAL") {
      if (f.consignado)
        errores.push("Factura: una consignación no se paga de contado; márquela a crédito.");
      if (f.tipodepago !== "Efectivo" && (!f.banco.trim() || !f.referencia.trim()))
        errores.push("Pago de contado: indique el banco y la referencia.");
    }
    if (conPlan) {
      const problema = problemaCuotas(planCuotas, valorTotalSumaCostos);
      if (problema) errores.push(`Plan de pagos: ${problema}`);
    }
    rows.forEach((row, idx) => {
      const faltas = problemasFila(row, configBulk);
      if (faltas.length > 0) {
        errores.push(`Fila ${idx + 1}: falta ${faltas.join(", ")}.`);
      }
    });
    return errores;
  };

  const imagenVista = useMemo(
    () => imagenes.find((x) => x.id === selectedId) ?? imagenes[0] ?? null,
    [imagenes, selectedId]
  );

  const excelVista = useMemo(
    () => excels.find((x) => x.id === selectedId) ?? excels[0] ?? null,
    [excels, selectedId]
  );

  const modoExcel = excels.length > 0;
  const totalArchivos = imagenes.length + excels.length;
  // Todos los Excel cargados traen la plantilla: nada pasa por la IA.
  const todoPlantilla = modoExcel && excels.every((e) => Array.isArray(e.items));

  const handleGuardar = () => {
    if (loading) return;
    // Rol con "ocultarcostos": no da de alta inventario (el recibo entra con
    // los costos de la compra). La pantalla ni siquiera ofrece el botón que
    // abre este modal; esto cierra el paso si se llega por otra vía.
    if (sinCostos) {
      setError("Su rol no puede agregar chasis al inventario porque no tiene acceso a los costos");
      return;
    }
    // QA: el botón vive habilitado; al pulsarlo se valida todo y, si hay
    // errores, se listan en pantalla sin guardar.
    const errores = erroresPaso2();
    if (errores.length > 0) {
      setError(errores.join("\n"));
      return;
    }
    setError(null);
    setChasisConflicto([]);
    const payload = {
      factura: {
        ...formFactura,
        suplidor_id: formFactura.suplidorId,
        tasa: parseNumero(formFactura.tasa),
        valorTotal: valorTotalSumaCostos,
        consignado: formFactura.consignado ? "S" : "N",
        tipodefactura: formFactura.tipodefactura,
        pago:
          formFactura.tipodefactura === "CONTADO" || formFactura.tipodefactura === "INFORMAL"
            ? {
                tipodepago: formFactura.tipodepago,
                banco: formFactura.banco.trim(),
                referencia: formFactura.referencia.trim(),
              }
            : undefined,
        plan: conPlan
          ? planCuotas.map((c) => ({ fecha: c.fecha, monto: parseNumero(c.monto) }))
          : undefined,
        usados: formFactura.usados ? "S" : "N",
        desarmados: formFactura.desarmados ? "S" : "N",
        vendedor: esInformal
          ? {
              documento: vendedor.documento,
              nombre: vendedor.nombre.trim(),
              telefono: vendedor.telefono.trim(),
              direccion: vendedor.direccion.trim(),
            }
          : undefined,
      },
      items: rows.map(({ id, manual, precioBase, costoBase, precioMinimo, ...rest }) => ({
        ...rest,
        mprecio: parseNumero(precioMinimo),
      })),
    };
    setLoading(true);
    post("/api/inventariobulk/save-receipt", {
      factura: payload.factura,
      items: payload.items,
    })
      .then(({ ok, data }) => {
        if (!ok) {
          // El backend detalla los chasis rechazados: ya existentes en
          // inventario o repetidos dentro del mismo envío. Se listan en el
          // mensaje y se resaltan sus filas.
          const conflicto = [
            ...(Array.isArray(data?.chasis_existentes) ? data.chasis_existentes : []),
            ...(Array.isArray(data?.chasis_duplicados_payload)
              ? data.chasis_duplicados_payload
              : []),
          ]
            .map(normalizarChasis)
            .filter(Boolean);
          if (conflicto.length > 0) {
            setChasisConflicto(conflicto);
            throw new Error(`${data?.Error || "Chasis en conflicto"}: ${conflicto.join(", ")}`);
          }
          throw new Error(data?.Error || "");
        }
        setSavePago(data?.pago ?? null);
        setSaveInformal(data?.informal ? String(data?.nfactura || "") : "");
        setSaveSuccessOpen(true);
        // Imprime la factura/conduce de la compra con el detalle recien guardado.
        // En la compra informal el número lo asignó el backend y el documento
        // es el recibo que firma el vendedor.
        const vendedorImpreso = payload.factura.vendedor;
        const pagoImpreso = payload.factura.pago;
        post("/api/cxp_factura_detalle", {
          nfactura: String(data?.nfactura || payload.factura.numFactura || "").trim(),
        })
          .then(({ ok: okDet, data: det }) => {
            if (!okDet) return;
            if (data?.informal) imprimirCompraInformal(det, vendedorImpreso, pagoImpreso);
            else imprimirFacturaCxp(det);
          })
          .catch(() => {});
        handleClose();
      })
      .catch((err) => {
        setError(err.message || "No se pudo guardar el recibo");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const chasisConflictoSet = useMemo(
    () =>
      new Set([...chasisConflicto, ...bloqueados.map((b) => normalizarChasis(b.chasis))]),
    [chasisConflicto, situaciones]
  );
  const claseFilaConflicto = (params) =>
    chasisConflictoSet.has(normalizarChasis(params.row?.chasis)) ? "fila-chasis-conflicto" : "";
  const sxFilaConflicto = {
    "& .fila-chasis-conflicto": {
      bgcolor: theme.palette.mode === "dark" ? "rgba(211,47,47,0.25)" : "rgba(211,47,47,0.12)",
    },
    "& .fila-chasis-conflicto:hover, & .fila-chasis-conflicto.Mui-hovered": {
      bgcolor: theme.palette.mode === "dark" ? "rgba(211,47,47,0.35)" : "rgba(211,47,47,0.2)",
    },
  };

  const gridHeight = { xs: "38vh", md: "calc(100vh - 320px)" };
  const imageMinHeight = { md: "calc(100vh - 320px)" };

  return (
    <>
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      fullScreen
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: theme.palette.background.default,
          backgroundImage: "none",
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          py: 2.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          background:
            "linear-gradient(135deg, #0d47a1 0%, #1565c0 38%, #1976d2 72%, #2196f3 100%)",
          color: theme.palette.common.white,
          boxShadow: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha("#fff", 0.15),
            }}
          >
            <Inventory2OutlinedIcon sx={{ fontSize: 28, color: theme.palette.common.white }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={600}
              letterSpacing={-0.3}
              sx={(t) => ({
                color: t.palette.common.white,
                "&.MuiTypography-h5": { color: t.palette.common.white },
              })}
            >
              Inventario bulk
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.25, color: alpha(theme.palette.common.white, 0.92) }}
            >
              {step === 1
                ? "Paso 1: cargue imágenes o Excel y extraiga los datos, o agregue los chasis a mano. Luego continúe para completar la factura."
                : "Paso 2: revise las filas y complete los datos de factura / suplidor."}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          aria-label="cerrar"
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: "inherit",
            bgcolor: alpha("#fff", 0.1),
            "&:hover": { bgcolor: alpha("#fff", 0.2) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {loading && (
        <LinearProgress
          color="inherit"
          sx={{
            height: 3,
            bgcolor: alpha("#0d47a1", 0.25),
            "& .MuiLinearProgress-bar": {
              transition: "transform 0.4s ease",
              background: "linear-gradient(90deg, #42a5f5, #90caf9)",
            },
          }}
        />
      )}

      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          pt: 3,
          px: { xs: 2, sm: 3 },
          pb: 2,
          bgcolor: theme.palette.background.default,
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={onPickFile}
        />
        <input
          ref={excelInputRef}
          type="file"
          accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
          multiple
          hidden
          onChange={onPickExcel}
        />

        {step === 1 && (
          <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            borderColor: alpha(theme.palette.divider, 0.9),
            bgcolor: "background.paper",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => inputRef.current?.click()}
                disabled={loading}
                startIcon={<AddPhotoAlternateOutlinedIcon />}
              >
                Elegir imágenes
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => excelInputRef.current?.click()}
                disabled={loading}
                startIcon={<UploadFileOutlinedIcon />}
              >
                Elegir Excel
              </MDButton>
              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={() =>
                  downloadTemplateXlsx("plantilla_productos.xlsx", [
                    ...PLANTILLA_PRODUCTOS_COLUMNS.slice(0, 5),
                    ...(configBulk.pideAno ? ["año"] : []),
                    ...(configBulk.pideCilindraje ? ["cilindraje"] : []),
                    ...PLANTILLA_PRODUCTOS_COLUMNS.slice(5),
                  ])
                }
                disabled={loading}
                startIcon={<DownloadOutlinedIcon />}
              >
                Descargar plantilla
              </MDButton>
              {imagenes.length > 0 && (
                <Chip
                  label={`${imagenes.length} imagen${imagenes.length === 1 ? "" : "es"}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {excels.length > 0 && (
                <Chip
                  label={`${excels.length} Excel`}
                  size="small"
                  variant="outlined"
                />
              )}
              {totalArchivos > 0 && (
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  onClick={limpiarArchivos}
                  disabled={loading}
                >
                  Quitar todos
                </MDButton>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 420 }}>
              Puede cargar varias imágenes (JPG, PNG…) o archivos Excel (.xlsx, .xls, .csv);
              no se mezclan: al elegir un tipo se descarta el otro. Si el Excel usa la
              plantilla de productos, todas sus filas se ingresan directo, sin IA y sin
              límite; con otro formato, la IA interpreta las columnas.
            </Typography>
          </Stack>
        </Paper>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2, whiteSpace: "pre-line" }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {loading && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <CircularProgress size={22} thickness={4} />
            <Typography variant="body2" color="text.secondary">
              {modoExcel
                ? `Analizando ${excels.length} archivo${excels.length === 1 ? "" : "s"} Excel…`
                : `Analizando ${imagenes.length} imagen${imagenes.length === 1 ? "" : "es"}…`}
            </Typography>
          </Stack>
        )}

        {step === 1 && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              flex: { xs: "none", md: "1 1 30%" },
              width: { xs: "100%", md: "auto" },
              minWidth: 0,
              borderRadius: 2,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
            }}
          >
            <SectionHeader
              icon={modoExcel ? UploadFileOutlinedIcon : ImageOutlinedIcon}
              title={modoExcel ? "Archivos Excel" : "Imágenes"}
              subtitle={
                modoExcel
                  ? `${excels.length} archivo${excels.length === 1 ? "" : "s"} · Pulse uno para ver su contenido`
                  : imagenes.length > 0
                    ? `${imagenes.length} archivo${imagenes.length === 1 ? "" : "s"} · Pulse una miniatura para ampliar`
                    : "Añada fotos del listado o un archivo Excel"
              }
            />
            <Box
              sx={{
                flex: 1,
                minHeight: { xs: 260, md: 0 },
                maxHeight: { xs: "42vh", md: "none" },
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                justifyContent: "flex-start",
                gap: 1.5,
              }}
            >
              {excels.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ overflowX: "auto", pb: 0.5, flexShrink: 0 }}
                >
                  {excels.map((ex) => (
                    <Chip
                      key={ex.id}
                      icon={<UploadFileOutlinedIcon />}
                      label={
                        Array.isArray(ex.items)
                          ? `${ex.file.name} · plantilla (${ex.items.length})`
                          : ex.file.name
                      }
                      size="small"
                      variant={
                        ex.id === (excelVista?.id ?? null) ? "filled" : "outlined"
                      }
                      color={ex.id === (excelVista?.id ?? null) ? "info" : "default"}
                      onClick={() => setSelectedId(ex.id)}
                      onDelete={() => removeExcel(ex.id)}
                      sx={{ flexShrink: 0, maxWidth: 220 }}
                    />
                  ))}
                </Stack>
              )}
              {imagenes.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 0.5, flexShrink: 0 }}>
                  {imagenes.map((img) => (
                    <Box
                      key={img.id}
                      sx={{
                        position: "relative",
                        flexShrink: 0,
                        width: 84,
                      }}
                    >
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setSelectedId(img.id)}
                        sx={{
                          p: 0,
                          m: 0,
                          display: "block",
                          border: 2,
                          borderColor:
                            (selectedId === img.id ||
                              (selectedId == null && imagenes[0]?.id === img.id))
                              ? "primary.main"
                              : "divider",
                          borderRadius: 1,
                          overflow: "hidden",
                          cursor: "pointer",
                          bgcolor: "background.paper",
                          width: "100%",
                        }}
                      >
                        <Box
                          component="img"
                          src={img.previewUrl}
                          alt=""
                          sx={{
                            width: 80,
                            height: 80,
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        aria-label="Quitar imagen"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          removeImagen(img.id);
                        }}
                        sx={{
                          position: "absolute",
                          top: -10,
                          right: -10,
                          bgcolor: "background.paper",
                          boxShadow: 1,
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
              {imagenVista ? (
                <Box
                  sx={{
                    width: "100%",
                    flex: 1,
                    minHeight: { xs: 220, ...imageMinHeight },
                    maxHeight: { xs: "42vh", md: "none" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 1.5,
                    overflow: "auto",
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[800]
                        : theme.palette.grey[100],
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Box
                    component="img"
                    src={imagenVista.previewUrl}
                    alt={imagenVista.file.name}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              ) : excelVista ? (
                <Box
                  sx={{
                    width: "100%",
                    flex: 1,
                    minHeight: { xs: 220, ...imageMinHeight },
                    maxHeight: { xs: "42vh", md: "none" },
                    borderRadius: 1.5,
                    overflow: "auto",
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[900]
                        : theme.palette.common.white,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Box
                    component="table"
                    sx={{
                      borderCollapse: "collapse",
                      width: "max-content",
                      minWidth: "100%",
                      "& td": {
                        border: 1,
                        borderColor: "divider",
                        px: 1,
                        py: 0.25,
                        fontSize: "0.75rem",
                        whiteSpace: "nowrap",
                        color: "text.primary",
                      },
                      "& tr:first-of-type td": {
                        fontWeight: 600,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[800]
                            : theme.palette.grey[100],
                      },
                    }}
                  >
                    <tbody>
                      {excelVista.preview.map((fila, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <tr key={i}>
                          {fila.map((celda, j) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <td key={j}>{String(celda ?? "")}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                </Box>
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{
                    width: "100%",
                    minHeight: imageMinHeight,
                    maxHeight: { xs: "42vh", md: "none" },
                    borderRadius: 1.5,
                    border: 2,
                    borderStyle: "dashed",
                    borderColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.primary.main, 0.5)
                        : theme.palette.grey[300],
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.primary.main, 0.08)
                        : theme.palette.grey[50],
                    py: 4,
                    px: 2,
                  }}
                >
                  <ImageOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", opacity: 0.7 }} />
                  <Typography variant="body2" color="text.secondary" align="center">
                    Aún no hay archivos
                  </Typography>
                  <Typography variant="caption" color="text.disabled" align="center">
                    Use «Elegir imágenes» o «Elegir Excel» para cargar archivos
                  </Typography>
                </Stack>
              )}
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                px: 2,
                py: 2,
                borderTop: 1,
                borderColor: "divider",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.grey[500], 0.08)
                    : theme.palette.grey[50],
              }}
            >
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                onClick={handleSubmit}
                disabled={loading || totalArchivos === 0}
              >
                {todoPlantilla ? "Ingresar filas" : "Extraer con IA"}
              </MDButton>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              flex: { xs: "none", md: "1 1 52%" },
              width: { xs: "100%", md: "auto" },
              minHeight: { xs: 200, md: 0 },
              minWidth: 0,
              borderRadius: 2,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
            }}
          >
            <SectionHeader
              icon={GridOnOutlinedIcon}
              title="Datos extraídos"
              subtitle={
                <>
                  <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                    {rows.length}
                  </Box>
                  {" chasis registrados · Doble clic en una celda para editar"}
                </>
              }
              action={
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={() => agregarChasisManual(gridPaso1Ref)}
                  disabled={loading}
                  startIcon={<AddOutlinedIcon />}
                >
                  Agregar chasis
                </MDButton>
              }
            />
            <Box sx={{ px: 1.5, pt: 1.5 }}>
              <Autocomplete
                options={sucursalOptions.map((o) => String(o?.label ?? ""))}
                value={sucursalGlobal || null}
                onChange={(_, value) => {
                  const label = value || "";
                  setSucursalGlobal(label);
                  if (label) {
                    setRows((prev) => prev.map((r) => ({ ...r, sucursal: label })));
                  }
                }}
                size="small"
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sucursal para todo el lote"
                    variant="outlined"
                    helperText={
                      sucursalGlobal
                        ? "Aplicada a todos los chasis del lote (puedes ajustar filas individuales)"
                        : "Opcional: al elegirla, todos los chasis del lote van a esa sucursal"
                    }
                  />
                )}
              />
            </Box>
            {rows.length > 0 ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  height: gridHeight,
                  width: "100%",
                  p: 1,
                  "& .MuiDataGrid-root": {
                    border: "none",
                    borderRadius: 1,
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[800]
                        : theme.palette.grey[100],
                    borderRadius: "8px 8px 0 0",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, fontSize: "0.8125rem" },
                  "& .MuiDataGrid-cell": { fontSize: "0.8125rem" },
                }}
              >
                <DataGrid
                  apiRef={gridPaso1Ref}
                  rows={rows}
                  columns={inventarioBulkGridColumns}
                  processRowUpdate={processRowUpdate}
                  disableRowSelectionOnClick
                  hideFooter
                  density="compact"
                  getRowClassName={claseFilaConflicto}
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-cell:focus-within": { outline: "none" },
                    ...sxFilaConflicto,
                  }}
                />
              </Box>
            ) : (
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{
                  flex: 1,
                  minHeight: gridHeight,
                  px: 2,
                  py: 3,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[900]
                      : theme.palette.grey[50],
                }}
              >
                <GridOnOutlinedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                <Typography color="text.secondary" align="center" variant="body2">
                  {result && rows.length === 0 && !loading
                    ? "No se extrajeron filas en este archivo."
                    : "Los datos aparecerán aquí después de enviar el archivo, o use «Agregar chasis» para digitarlos a mano."}
                </Typography>
              </Stack>
            )}
          </Paper>
        </Box>
        )}

        {step === 2 && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: "stretch",
            }}
          >
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                flex: { xs: "none", md: "1 1 52%" },
                width: { xs: "100%", md: "auto" },
                minHeight: { xs: 200, md: 0 },
                minWidth: 0,
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper",
              }}
            >
              <SectionHeader
                icon={GridOnOutlinedIcon}
                title="Datos extraídos"
                subtitle={
                  <>
                    <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                      {rows.length} chasis registrados
                    </Box>
                    {" · Sucursal por fila · Doble clic en una celda para editar"}
                  </>
                }
                action={
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() => agregarChasisManual(gridPaso2Ref)}
                    disabled={loading}
                    startIcon={<AddOutlinedIcon />}
                  >
                    Agregar chasis
                  </MDButton>
                }
              />
              <AvisoMarcasEstandar
                filas={rows}
                estandar={estandar}
                onUsar={usarSugerenciaMarca}
                sx={{ mx: 1 }}
              />
              {rows.length > 0 ? (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    height: gridHeight,
                    width: "100%",
                    p: 1,
                    "& .MuiDataGrid-root": {
                      border: "none",
                      borderRadius: 1,
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? theme.palette.grey[800]
                          : theme.palette.grey[100],
                      borderRadius: "8px 8px 0 0",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, fontSize: "0.8125rem" },
                    "& .MuiDataGrid-cell": { fontSize: "0.8125rem" },
                  }}
                >
                  <DataGrid
                    apiRef={gridPaso2Ref}
                    rows={rows}
                    columns={inventarioBulkGridColumns}
                    processRowUpdate={processRowUpdate}
                    disableRowSelectionOnClick
                    hideFooter
                    density="compact"
                    getRowClassName={claseFilaConflicto}
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-cell:focus-within": { outline: "none" },
                      ...sxFilaConflicto,
                    }}
                  />
                </Box>
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{
                    flex: 1,
                    minHeight: gridHeight,
                    px: 2,
                    py: 3,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[900]
                        : theme.palette.grey[50],
                  }}
                >
                  <Typography color="text.secondary" align="center" variant="body2">
                    No hay filas. Use «Agregar chasis» o vuelva al paso 1 para extraer datos.
                  </Typography>
                </Stack>
              )}
            </Paper>

            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                flex: { xs: "none", md: "1 1 48%" },
                width: { xs: "100%", md: "auto" },
                minWidth: 0,
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper",
              }}
            >
              <SectionHeader
                icon={ReceiptLongOutlinedIcon}
                title="Factura y suplidor"
                subtitle="Complete los campos antes de registrar"
              />
              <Stack spacing={2} sx={{ p: 2, flex: 1, overflow: "auto" }}>
                <TextField
                  select
                  label="Condición de la compra"
                  value={formFactura.tipodefactura}
                  onChange={(e) =>
                    setFormFactura((p) => ({ ...p, tipodefactura: e.target.value }))
                  }
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={selectSmallSx}
                  helperText={
                    formFactura.tipodefactura === "INFORMAL"
                      ? "Sin factura del vendedor: se paga al guardar y se imprime el recibo para su firma"
                      : formFactura.tipodefactura === "CONTADO"
                        ? "Se registra el pago al suplidor por el total al guardar"
                        : "Queda en cuentas por pagar hasta que se pague"
                  }
                >
                  <MenuItem value="CREDITO">A crédito</MenuItem>
                  <MenuItem value="CONTADO" disabled={!!formFactura.consignado}>
                    Al contado
                  </MenuItem>
                  <MenuItem value="INFORMAL" disabled={!!formFactura.consignado}>
                    Compra informal (sin factura)
                  </MenuItem>
                </TextField>
                {esInformal && (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                    <Stack spacing={1.5}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        VENDEDOR
                      </Typography>
                      <Box>
                        <DocumentoField
                          size="small"
                          label="Documento del vendedor"
                          value={vendedor.documento}
                          tipo={vendedor.tipo}
                          onChangeValor={(v) => setVendedor((p) => ({ ...p, documento: v }))}
                          onChangeTipo={(t) => setVendedor((p) => ({ ...p, tipo: t }))}
                          helperText={
                            vendedorOrigen === "suplidor"
                              ? "Ya registrado: la compra va a su cuenta"
                              : vendedorOrigen === "cliente"
                                ? "Datos precargados del cliente con este documento"
                                : undefined
                          }
                        />
                        <AlertaRed
                          cedula={vendedor.documento}
                          tipo={vendedor.tipo}
                          sx={{ mt: 0.75 }}
                        />
                      </Box>
                      <TextField
                        label="Nombre completo"
                        value={vendedor.nombre}
                        onChange={(e) => setVendedor((p) => ({ ...p, nombre: e.target.value }))}
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                        <TextField
                          label="Teléfono"
                          value={vendedor.telefono}
                          onChange={(e) =>
                            setVendedor((p) => ({ ...p, telefono: e.target.value }))
                          }
                          fullWidth
                          size="small"
                          variant="outlined"
                          inputProps={{ inputMode: "tel", maxLength: 15 }}
                        />
                        <TextField
                          label="Dirección"
                          value={vendedor.direccion}
                          onChange={(e) =>
                            setVendedor((p) => ({ ...p, direccion: e.target.value }))
                          }
                          fullWidth
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>
                  </Paper>
                )}
                {esInformal && bloqueados.length > 0 && (
                  <Alert severity="error" sx={{ borderRadius: 1.5 }}>
                    {bloqueados.map((b) => (
                      <div key={b.chasis}>
                        <b>{b.chasis}</b>: {b.detalle}
                      </div>
                    ))}
                  </Alert>
                )}
                {esInformal && recompras.length > 0 && (
                  <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                    {recompras.map((r) => (
                      <div key={r.chasis}>
                        <b>{r.chasis}</b>: {r.detalle}. Vuelve al inventario como usado.
                      </div>
                    ))}
                  </Alert>
                )}
                {!esInformal && (
                <Autocomplete
                  freeSolo
                  options={supplierOptions}
                  value={formFactura.suplidor}
                  onChange={(event, newValue) => {
                    if (newValue && typeof newValue === "object") {
                      setFormFactura((p) => ({
                        ...p,
                        suplidor: String(newValue.label ?? ""),
                        suplidorId: String(newValue.id ?? ""),
                      }));
                      return;
                    }
                    setFormFactura((p) => ({
                      ...p,
                      suplidor: newValue != null ? String(newValue) : "",
                      suplidorId: "",
                    }));
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input" || reason === "clear") {
                      setFormFactura((p) => ({
                        ...p,
                        suplidor: newInputValue,
                        suplidorId: "",
                      }));
                    }
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option?.label ?? ""
                  }
                  isOptionEqualToValue={(option, value) => {
                    const optionId = typeof option === "string" ? option : option?.id;
                    const valueId = typeof value === "string" ? value : value?.id;
                    return String(optionId ?? "") === String(valueId ?? "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Suplidor"
                      size="small"
                      variant="outlined"
                      placeholder="Buscar o escribir…"
                    />
                  )}
                />
                )}
                {multiempresaBulk.activo && (
                  <TextField
                    select
                    label="Empresa"
                    value={formFactura.empresa}
                    onChange={(e) =>
                      setFormFactura((p) => ({ ...p, empresa: e.target.value }))
                    }
                    fullWidth
                    size="small"
                    variant="outlined"
                    sx={selectSmallSx}
                  >
                    {multiempresaBulk.empresas.map((emp) => (
                      <MenuItem key={emp.empresa} value={String(emp.empresa)}>
                        {emp.empresa} — {emp.ncomp || `Empresa ${emp.empresa}`}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {esInformal ? (
                  <TextField
                    label="# Compra"
                    value="Se asigna al guardar (CI-…)"
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{ readOnly: true }}
                  />
                ) : (
                <TextField
                  label="# FACTURA"
                  value={formFactura.numFactura}
                  onChange={(e) => {
                    setFacturaDuplicada(false);
                    setFacturaDuplicadaDe("");
                    setFormFactura((p) => ({ ...p, numFactura: e.target.value }));
                  }}
                  onBlur={verificarFacturaDuplicada}
                  error={facturaDuplicada}
                  helperText={
                    facturaDuplicada
                      ? `Este número de factura ya está registrado${
                          facturaDuplicadaDe ? ` (${facturaDuplicadaDe})` : ""
                        }`
                      : undefined
                  }
                  fullWidth
                  size="small"
                  variant="outlined"
                />
                )}
                <TextField
                  label="Fecha"
                  type="date"
                  value={formFactura.fecha}
                  onChange={(e) =>
                    setFormFactura((p) => ({ ...p, fecha: e.target.value }))
                  }
                  fullWidth
                  size="small"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Tasa"
                  type="number"
                  value={formFactura.tasa}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormFactura((p) => ({ ...p, tasa: v }));
                    const t = esNumeroValido(v) ? parseNumero(v) : null;
                    setRows((prev) =>
                      prev.map((row) => {
                        const pb = row.precioBase ?? parseNumeroLoose(row.precio) ?? 0;
                        const cb = row.costoBase ?? parseNumeroLoose(row.costo) ?? 0;
                        if (t != null && t !== 0) {
                          return {
                            ...row,
                            precioBase: pb,
                            costoBase: cb,
                            precio: formatMonto(pb),
                            costo: formatMonto(cb * t),
                          };
                        }
                        return {
                          ...row,
                          precioBase: pb,
                          costoBase: cb,
                          precio: formatMonto(pb),
                          costo: formatMonto(cb),
                        };
                      })
                    );
                  }}
                  fullWidth
                  size="small"
                  variant="outlined"
                  inputProps={{
                    step: "any",
                    min: 0,
                    inputMode: "decimal",
                  }}
                />
                {!sinCostos && (
                  <TextField
                    label="Valor total (suma de costos)"
                    value={formatMonto(valorTotalSumaCostos)}
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{ readOnly: true }}
                    helperText="Calculado automáticamente"
                  />
                )}
                <TextField
                  label={esInformal ? "Concepto (opcional)" : "Concepto"}
                  value={formFactura.concepto}
                  onChange={(e) =>
                    setFormFactura((p) => ({ ...p, concepto: e.target.value }))
                  }
                  fullWidth
                  size="small"
                  variant="outlined"
                  multiline
                  minRows={3}
                  placeholder={esInformal ? "COMPRA INFORMAL A <vendedor>" : undefined}
                />
                {(formFactura.tipodefactura === "CONTADO" || esInformal) && (
                  <>
                    <TextField
                      select
                      label="Forma de pago"
                      value={formFactura.tipodepago}
                      onChange={(e) =>
                        setFormFactura((p) => ({ ...p, tipodepago: e.target.value }))
                      }
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={selectSmallSx}
                    >
                      {tiposPago.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </TextField>
                    {formFactura.tipodepago !== "Efectivo" && (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                          label="Banco"
                          value={formFactura.banco}
                          onChange={(e) =>
                            setFormFactura((p) => ({ ...p, banco: e.target.value }))
                          }
                          fullWidth
                          size="small"
                          variant="outlined"
                        />
                        <TextField
                          label="Referencia"
                          value={formFactura.referencia}
                          onChange={(e) =>
                            setFormFactura((p) => ({ ...p, referencia: e.target.value }))
                          }
                          fullWidth
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    )}
                  </>
                )}
                {planAplica && (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={planActivo}
                          onChange={(e) => {
                            const activo = e.target.checked;
                            setPlanActivo(activo);
                            if (activo && planCuotas.length === 0)
                              setPlanCuotas(
                                generarCuotas(valorTotalSumaCostos, 3, "mensual", manana())
                              );
                          }}
                          color="info"
                        />
                      }
                      label="Plan de pagos al suplidor"
                    />
                    {planActivo ? (
                      <Box sx={{ mt: 1 }}>
                        <EditorCuotas
                          balance={valorTotalSumaCostos}
                          cuotas={planCuotas}
                          setCuotas={setPlanCuotas}
                          disabled={loading}
                          etiquetaTotal="el valor total"
                        />
                        <Typography variant="caption" color="text.secondary">
                          Si cambia los costos de las filas, pulse «Generar» para repartir el
                          nuevo total.
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary" component="div">
                        Reparta el total en cuotas con fecha; también se puede hacer después en el
                        detalle de la factura.
                      </Typography>
                    )}
                  </Paper>
                )}
                <FormControlLabel
                  disabled={esInformal}
                  control={
                    <Switch
                      checked={!!formFactura.consignado}
                      onChange={(e) =>
                        setFormFactura((p) => ({
                          ...p,
                          consignado: e.target.checked,
                          // Una consignación no se paga de contado.
                          tipodefactura: e.target.checked ? "CREDITO" : p.tipodefactura,
                        }))
                      }
                      color="info"
                    />
                  }
                  label={formFactura.consignado ? "Consignado" : "No consignado"}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formFactura.usados}
                      onChange={(e) =>
                        setFormFactura((p) => ({ ...p, usados: e.target.checked }))
                      }
                      color="info"
                    />
                  }
                  label={formFactura.usados ? "Chasis usados" : "Chasis nuevos"}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formFactura.desarmados}
                      onChange={(e) =>
                        setFormFactura((p) => ({ ...p, desarmados: e.target.checked }))
                      }
                      color="info"
                    />
                  }
                  label={
                    formFactura.desarmados
                      ? "Motores desarmados (entran al taller, no disponibles para la venta)"
                      : "Motores listos para la venta"
                  }
                />
              </Stack>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          gap: 1.5,
        }}
      >
        {step === 2 && (
          <MDButton variant="outlined" color="dark" onClick={() => setStep(1)} disabled={loading}>
            Atrás
          </MDButton>
        )}
        <MDButton variant="outlined" color="dark" onClick={handleClose} disabled={loading}>
          Cerrar
        </MDButton>
        <Box sx={{ flex: 1 }} />
        {step === 1 && rows.length > 0 && (
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => setStep(2)}
            disabled={loading}
          >
            Continuar
          </MDButton>
        )}
        {step === 2 && (
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleGuardar}
            disabled={loading}
          >
            Guardar
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
    <Snackbar
      open={saveSuccessOpen}
      autoHideDuration={6500}
      onClose={(_, reason) => {
        if (reason === "clickaway") return;
        setSaveSuccessOpen(false);
      }}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Alert
        onClose={() => setSaveSuccessOpen(false)}
        severity="success"
        variant="filled"
        sx={{
          minWidth: { xs: "100%", sm: 380 },
          alignItems: "center",
          fontWeight: 500,
          borderRadius: 2,
          boxShadow: theme.shadows[8],
          color: theme.palette.common.white,
          "& .MuiAlert-message": { color: theme.palette.common.white },
          "& .MuiAlert-icon": { color: theme.palette.common.white },
          "& .MuiAlert-action .MuiIconButton-root": { color: theme.palette.common.white },
        }}
      >
        {saveInformal
          ? `Compra informal ${saveInformal} guardada y pagada${savePago ? ` (pago #${savePago})` : ""}.`
          : savePago
            ? `Registro guardado correctamente. Compra pagada de contado (pago #${savePago}).`
            : "Registro guardado correctamente."}
      </Alert>
    </Snackbar>
    </>
  );
}

export default InventariobulkModal;
